import {
  Avatar,
  Button,
  CircularProgress,
  Switch,
  Tag,
  Tooltip,
  Typography,
} from "@suraasa/placebo-ui"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { useCopyToClipboard } from "@uidotdev/usehooks"
import api from "api"
import { AIDetectionStatus } from "api/resources/submissions/types"
import { APIError } from "api/utils"
import clsx from "clsx"
import {
  AttachmentAIBadge,
  RunAttachmentAI,
  useAttachmentAI,
} from "components/AttachmentAI"
import Navbar from "components/Navbar"
import PageContainer from "components/PageContainer"
import BackButton from "components/shared/BackButton"
import { Attachment, Check, Copy, ViewGrid } from "iconoir-react"
import { useEffect, useState } from "react"
import { Link, useParams } from "react-router-dom"
import { formatDate, getPlagiarismStatus } from "utils/helpers"
import useArray from "utils/hooks/useArray"
import { useSkillEvidenceStyles } from "utils/hooks/useSkillEvidenceStyles"
import { isPIIRedactionEnabled, redactPII } from "utils/pii"
import { getProduct } from "utils/products"

import TaskDescription from "../TaskDescription"
import GrammarlyReport from "./GrammarlyReport"
import { CheckBanner } from "./RetryChecks"
import RubricSheet from "./RubricSheet"
import SubmissionContent, { SubmissionViewMode } from "./SubmissionContent"
import { useQueuedChecks } from "./useQueuedChecks"

type SelectedRubric = { rubricId: string; commentId: string }

const POLL_INTERVAL = 12_000
// AI usage from here on gets a mark deduction, so the banner flags it
const AI_FLAG_THRESHOLD = 50
// Long enough for hades to move a re-queued check out of "failed" or import a missing submission
const FAILED_GRACE_PERIOD = 30_000

export type CurrentReview = {
  comment: string
} & {
  selected: SelectedRubric[]
}

const ViewSubmission = () => {
  const styles = useSkillEvidenceStyles()
  const params = useParams() as { id: string }
  const initId = params.id
  const isRedacted = isPIIRedactionEnabled()

  const [id, setId] = useState(initId)

  const [showRubrics, setShowRubrics] = useState(false)
  const [viewMode, setViewMode] = useState<SubmissionViewMode>("default")

  const selectedRubrics = useArray<SelectedRubric>([])
  const [currentReview, setCurrentReview] = useState<
    Omit<CurrentReview, "selected">
  >({
    comment: "",
  })

  const {
    isLoading: submissionLoading,
    data: submission,
    refetch: refetchSubmission,
  } = useQuery({
    queryKey: ["submission", id],
    queryFn: () =>
      api.submissions.retrieve({
        urlParams: {
          submissionId: id,
        },
        params: {
          fields: [
            "review",
            "iqa_review",
            "review_rubrics",
            "courses",
            "iqa_review_rubrics",
            "submitted_at",
            "citations",
          ],
        },
      }),
  })

  const { data: submissionData, refetch: refetchSubmissionData } = useQuery({
    queryKey: [
      "assignment-user-submissions-list",
      submission?.assignmentId,
      submission?.userId,
    ],
    queryFn: () =>
      api.submissions.listAssignmentUserSubmissions({
        urlParams: {
          assignmentId: submission?.assignmentId || "",
          userId: submission?.userId || "",
        },
        params: {
          page: "all",
        },
      }),
    enabled: () => !!(submission?.assignmentId && submission?.userId),
  })

  const { data: rubricsData, refetch: refetchRubrics } = useQuery({
    queryKey: ["assignment-rubrics", submission?.assignmentId],
    queryFn: () =>
      api.review.rubrics.list({
        urlParams: {
          assignmentId: submission?.assignmentId || "",
        },
        params: {
          page: "all",
        },
      }),
    enabled: () => !!submission?.assignmentId,
  })

  const { isQueued, queuedAt, setCheckQueued } = useQueuedChecks(id)

  const {
    data: plagiarismData,
    error: plagiarismError,
    refetch: refetchPlagiarism,
    dataUpdatedAt: plagiarismUpdatedAt,
    errorUpdatedAt: plagiarismErrorUpdatedAt,
  } = useQuery({
    queryKey: ["plagiarism-report", id],
    queryFn: () =>
      api.submissions.getPlagiarismReport({
        urlParams: {
          submissionId: id,
        },
      }),
    // No push when a check finishes, so poll until it's done
    refetchInterval: query =>
      isQueued("internal") ||
      getPlagiarismStatus(query.state.data) === "in_progress"
        ? POLL_INTERVAL
        : false,
  })
  const {
    data: aiDetectedReportData,
    error: aiDetectedReportError,
    dataUpdatedAt: aiReportUpdatedAt,
    errorUpdatedAt: aiReportErrorUpdatedAt,
  } = useQuery({
    queryKey: ["ai-detected-report-data", id],
    queryFn: () =>
      api.submissions.getAiDetectedSubmissionReport({
        urlParams: {
          submissionId: id,
        },
      }),
    refetchInterval: query =>
      isQueued("ai") || query.state.data?.aiCheckStatus === "in_progress"
        ? POLL_INTERVAL
        : false,
  })

  const aiCheckStatus = aiDetectedReportData?.aiCheckStatus
  const aiQueuedAt = queuedAt("ai")
  const internalStatus = getPlagiarismStatus(plagiarismData)
  const internalQueuedAt = queuedAt("internal")
  // A re-queued check only bridges the gap until hades reports it, then hades's status drives the UI.
  // "failed" or a 404 fetched soon after queuing can be the old result, so they only count after the grace period
  useEffect(() => {
    const reported = (
      status: AIDetectionStatus | undefined,
      missing: boolean,
      fetchedAt: number,
      queuedTime: number | undefined
    ) =>
      queuedTime !== undefined &&
      (status === "in_progress" ||
        status === "completed" ||
        ((status === "failed" || missing) &&
          fetchedAt - queuedTime > FAILED_GRACE_PERIOD))

    const is404 = (e: unknown) => e instanceof APIError && e.statusCode === 404

    if (
      reported(
        aiCheckStatus,
        is404(aiDetectedReportError),
        Math.max(aiReportUpdatedAt, aiReportErrorUpdatedAt),
        aiQueuedAt
      )
    )
      setCheckQueued(["ai"], false)
    if (
      reported(
        internalStatus,
        is404(plagiarismError),
        Math.max(plagiarismUpdatedAt, plagiarismErrorUpdatedAt),
        internalQueuedAt
      )
    )
      setCheckQueued(["internal"], false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    aiCheckStatus,
    aiDetectedReportError,
    aiReportUpdatedAt,
    aiReportErrorUpdatedAt,
    aiQueuedAt,
    internalStatus,
    plagiarismError,
    plagiarismUpdatedAt,
    plagiarismErrorUpdatedAt,
    internalQueuedAt,
  ])

  // An empty 404 means hades doesn't have this submission yet
  const hadesMissing = [plagiarismError, aiDetectedReportError].some(
    e => e instanceof APIError && e.statusCode === 404
  )
  const attachmentAI = useAttachmentAI(
    submission?.uuid,
    submission?.attachments
  )

  const aiDone = aiCheckStatus === "completed"
  const internalDone = internalStatus === "completed"

  const isPlagiarised = plagiarismData?.plagiarismDetected

  const plagiarismPercentage = plagiarismData?.overallPlagiarismScore

  const plagiarismPct = Number(plagiarismPercentage ?? 0).toFixed(0)
  const aiPct = Number(aiDetectedReportData?.aiWrittenPercentage ?? 0)
  // Below this the AI usage carries no mark deduction (see RubricSheet), so it reads as not detected
  const aiNotDetected = aiDone && aiPct < AI_FLAG_THRESHOLD

  const rubrics = rubricsData
    ? rubricsData.map(r => {
        const review =
          submission?.reviewRubrics?.find(review => r.id === review.id) || {}
        return { ...r, ...review }
      })
    : []

  const { data: suggestedRubrics } = useQuery({
    queryKey: ["assignment-rubrics-ai-suggestions", initId],
    queryFn: () =>
      api.review.rubrics.getAISuggestions({
        urlParams: {
          submissionId: initId || "",
        },
      }),
    enabled: showRubrics,
  })

  const queryClient = useQueryClient()

  const onCopyFromPrevious = async () => {
    const previousSubmissionId = submissionData && submissionData[1]?.uuid
    if (!previousSubmissionId) {
      return
    }

    try {
      const data = await queryClient.fetchQuery({
        queryKey: ["submission", previousSubmissionId],
        queryFn: () =>
          api.submissions.retrieve({
            urlParams: {
              submissionId: previousSubmissionId,
            },
            params: {
              fields: ["review_rubrics", "review"],
            },
          }),
      })

      if (data.reviewRubrics) {
        selectedRubrics.set(
          data.reviewRubrics.map(r => ({
            commentId:
              r.allComments.find(c => c.uuid === r.marked?.uuid)?.uuid || "",
            rubricId: r.id,
          }))
        )
      }
      if (data.review) {
        setCurrentReview(d => ({
          ...d,
          comment: data.review?.comments || "",
        }))
      }

      // This event is consumed inside the RubricSheet to populate the penalties when copying previous review
      if (data.review?.penalties) {
        const event = new CustomEvent("reset-penalties", {
          detail: data.review.penalties,
        })
        document.dispatchEvent(event)
      }
    } catch (error) {}
  }

  const onCellClick = (rubricId: string, commentId: string) => {
    const data = selectedRubrics.array
    if (data.find(s => s.rubricId === rubricId)) {
      if (
        data.find(s => s.rubricId === rubricId && s.commentId === commentId)
      ) {
        selectedRubrics.removeByKey(commentId, "commentId")
      } else {
        selectedRubrics.updateByKey(
          rubricId,
          { commentId, rubricId },
          "rubricId"
        )
      }
    } else {
      selectedRubrics.push({
        commentId,
        rubricId,
      })
    }
  }

  const onCommentUpdate = (comment: string) => {
    setCurrentReview(d => ({
      ...d,
      comment: comment,
    }))
  }

  const [copiedText, copyToClipboard] = useCopyToClipboard()

  const copySubmissionToClipboard = () => {
    const text = document.querySelector("#submission-text")
    if (text) copyToClipboard(text.textContent || "")
  }

  const onFixMargins = () => {
    const richTextElements = document.querySelectorAll(".suraasa-se-rich-text")
    richTextElements.forEach(element => {
      element.classList.toggle("fix-negative-margins")
    })
  }

  return (
    <div className="min-h-screen pb-4">
      {rubrics && (
        <RubricSheet
          open={showRubrics}
          submissionLoading={submissionLoading}
          onClose={() => setShowRubrics(false)}
          data={submission}
          // @ts-expect-error to be fixed
          rubrics={rubrics}
          setId={setId}
          submissionData={submissionData}
          submissionId={id}
          onCopyFromPrevious={onCopyFromPrevious}
          onCellClick={onCellClick}
          suggestedRubrics={suggestedRubrics}
          currentReview={{
            ...currentReview,
            selected: selectedRubrics.array,
          }}
          onCommentUpdate={onCommentUpdate}
          refresh={async () => {
            await refetchSubmissionData()
            await refetchSubmission()
            await refetchRubrics()
          }}
          aiDetectedReportData={aiDetectedReportData}
        />
      )}
      <Navbar gutterBottom={false} hideBackButton />
      <div className="divide-y divide-onSurface-200">
        {/* Hidden until the first fetch settles, so a finished check doesn't flash its button */}
        {(plagiarismData || plagiarismError) && (
          <CheckBanner
            check="internal"
            submissionId={id}
            hadesMissing={hadesMissing}
            result={
              internalDone
                ? isPlagiarised
                  ? {
                      tone: "bg-warning-100 text-warning-900",
                      message:
                        "This submission seems to be plagiarized. Please evaluate the assignment accordingly.",
                      value: `Overall Plagiarism: ${plagiarismPct}%`,
                    }
                  : {
                      tone: "bg-success-50 text-success-900",
                      message: "No plagiarism detected in this submission.",
                    }
                : undefined
            }
            showRetry={
              !isQueued("internal") &&
              !internalDone &&
              (hadesMissing ||
                internalStatus === "failed" ||
                internalStatus === "not_started")
            }
            running={
              (isQueued("internal") || internalStatus === "in_progress") &&
              !internalDone
            }
            onQueued={checks => setCheckQueued(checks, true)}
          />
        )}
        {(aiDetectedReportData || aiDetectedReportError) && (
          <CheckBanner
            check="ai"
            submissionId={id}
            hadesMissing={hadesMissing}
            result={
              aiDone
                ? {
                    tone: clsx({
                      "bg-success-50 text-success-900": aiNotDetected,
                      "bg-warning-50 text-warning-900":
                        !aiNotDetected && aiPct < 80,
                      "bg-critical-50 text-critical-900": aiPct >= 80,
                    }),
                    message: aiNotDetected
                      ? "No significant AI usage detected in this submission."
                      : "This submission should be evaluated with consideration of detected AI usage.",
                    value: `AI usage: ${aiPct.toFixed(0)}%`,
                  }
                : undefined
            }
            showRetry={
              !isQueued("ai") &&
              !aiDone &&
              (hadesMissing ||
                aiCheckStatus === "failed" ||
                aiCheckStatus === "not_started")
            }
            running={
              (isQueued("ai") || aiCheckStatus === "in_progress") && !aiDone
            }
            onQueued={checks => setCheckQueued(checks, true)}
          />
        )}
      </div>

      <PageContainer>
        <div className="mt-2 flex flex-col gap-2">
          <BackButton />
          {submission && (
            <>
              <div className="flex flex-wrap items-center justify-between gap-1">
                <div className="flex flex-wrap items-center gap-1">
                  <Avatar
                    src={isRedacted ? undefined : submission.user.photo}
                    name={isRedacted ? undefined : submission.user.fullName}
                    className="size-5"
                  />
                  <div className="flex flex-col">
                    <Typography variant="title3">
                      {isRedacted
                        ? redactPII(submission.user.fullName)
                        : submission.user.fullName}
                    </Typography>
                    <Typography
                      variant="smallBody"
                      className="text-secondary-500"
                    >
                      {isRedacted
                        ? redactPII(submission.user.email)
                        : submission.user.email}
                    </Typography>

                    <div className="mt-1 flex flex-wrap items-center gap-1">
                      {submission.courses?.map(course => (
                        <Tag key={course}>{course}</Tag>
                      ))}
                      {submission.additionalDetails && (
                        <Tag>
                          {
                            getProduct({
                              deliveryMode:
                                submission.additionalDetails.deliveryMode,
                              id: submission.sourceParentId,
                              type: submission.sourceParentType,
                            })?.name
                          }
                        </Tag>
                      )}
                    </div>
                  </div>
                </div>
                {submission.submittedAt && (
                  <Typography
                    variant="smallBody"
                    className="text-secondary-500"
                  >
                    Submitted on {formatDate(submission.submittedAt)}
                  </Typography>
                )}
              </div>
              <TaskDescription
                assignment={submission.assignment}
                richTextClassName={styles.className}
              />
              {plagiarismData && (
                <GrammarlyReport
                  submissionId={id}
                  reportUrl={plagiarismData.grammarlyReport}
                  onUploaded={refetchPlagiarism}
                />
              )}
              <div className="flex flex-col gap-1.5 overflow-x-auto rounded-xl border border-onSurface-300 p-2">
                <div className="flex flex-wrap justify-between gap-1">
                  <div className="flex flex-wrap items-center gap-1">
                    <Typography variant="title4">
                      Assignment Submission
                    </Typography>

                    <Button
                      startAdornment={copiedText ? <Check /> : <Copy />}
                      variant="text"
                      size="sm"
                      onClick={copySubmissionToClipboard}
                      color={copiedText ? "success" : "primary"}
                      className="px-0.5 py-0.25"
                    >
                      {copiedText ? "Copied" : "Copy Submission"}
                    </Button>
                    <Tooltip content="Applies a margin fix to the content. May not work for all submissions.">
                      <Button
                        variant="text"
                        size="sm"
                        onClick={onFixMargins}
                        className="px-0.5 py-0.25"
                      >
                        Fix margins
                      </Button>
                    </Tooltip>
                  </div>
                  <div className="flex items-center gap-2">
                    {isPlagiarised && plagiarismData?.updatedHtml && (
                      <div className="flex items-center gap-1">
                        <Typography variant="smallBody">
                          Highlight Plagiarism
                        </Typography>{" "}
                        <Switch
                          size="xs"
                          checked={viewMode === "plagiarism"}
                          onCheckedChange={val => {
                            setViewMode(val ? "plagiarism" : "default")
                          }}
                        />
                      </div>
                    )}
                    <div className="flex items-center gap-1">
                      <Typography variant="smallBody">AI Detection</Typography>{" "}
                      <Switch
                        size="xs"
                        checked={viewMode === "ai"}
                        onCheckedChange={val => {
                          setViewMode(val ? "ai" : "default")
                        }}
                      />
                    </div>
                  </div>
                </div>
                <SubmissionContent
                  plagiarismData={plagiarismData}
                  submission={submission.submission}
                  submissionId={id}
                  viewMode={viewMode}
                  aiDetectedReportData={aiDetectedReportData}
                />

                {submission.attachments.length > 0 && (
                  <>
                    <hr />
                    <div>
                      {submission.attachments.map((a, i) => (
                        <div key={i} className="flex items-center gap-1">
                          <Link to={a.file} target="_blank" className="flex">
                            <div className="flex items-center gap-1">
                              <div className="rounded border p-1">
                                <Attachment className="size-2.5" />
                              </div>
                              <Typography variant="strongSmallBody">
                                {a.name}
                              </Typography>
                            </div>
                          </Link>
                          <AttachmentAIBadge
                            result={attachmentAI.get(a.name)}
                          />
                          <RunAttachmentAI
                            submissionId={id}
                            attachment={a}
                            result={attachmentAI.get(a.name)}
                          />
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
              {isPlagiarised && plagiarismData?.plagiarisedSourcesUsers && (
                <div className="flex flex-col gap-1.5 overflow-x-auto rounded-xl border border-onSurface-300 p-2">
                  <Typography variant="title4">
                    Plagiarised Sources (
                    {plagiarismData.plagiarisedSourcesUsers.length})
                  </Typography>
                  <div className="grid grid-cols-1 gap-1 sm:grid-cols-2 md:grid-cols-3">
                    {plagiarismData.plagiarisedSourcesUsers.map((e, i) => {
                      return (
                        <div key={i} className="flex items-center">
                          <Avatar
                            name={`${Number(i + 1)
                              .toString()
                              .split("")
                              .join(" ")}`}
                            className="me-0.5 size-4 rounded-full"
                          />
                          <Typography>
                            {!e.user
                              ? `Source ${i + 1}`
                              : isRedacted
                              ? redactPII(e.user.fullName)
                              : e.user.fullName}
                          </Typography>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
              {submission.references && (
                <div className="flex flex-col gap-1.5 overflow-x-auto rounded-xl border border-onSurface-300 p-2">
                  <Typography variant="title4">References</Typography>
                  <div
                    className={clsx(styles.className, "break-words")}
                    dangerouslySetInnerHTML={{
                      __html: submission.references,
                    }}
                  />
                </div>
              )}

              {submission.citations && (
                <div className="flex flex-col gap-1.5 overflow-x-auto rounded-xl border border-onSurface-300 p-2">
                  <Typography variant="title4">Citations</Typography>
                  <div
                    className={clsx(styles.className, "break-words")}
                    dangerouslySetInnerHTML={{
                      __html: submission.citations,
                    }}
                  />
                </div>
              )}
            </>
          )}
          {!showRubrics && submission && rubrics?.length > 0 && (
            <div className="fixed bottom-2 right-2  cursor-pointer rounded-full">
              <Button
                startAdornment={<ViewGrid />}
                onClick={() => setShowRubrics(true)}
                size="lg"
                rounded
                className="shadow-lg"
              >
                View Rubrics
              </Button>
            </div>
          )}
        </div>
        {submissionLoading && (
          <div className="my-2 flex justify-center">
            <CircularProgress className="size-4" />
          </div>
        )}
      </PageContainer>
    </div>
  )
}

export default ViewSubmission
